import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyMembership, getDeals } from "../lib/api";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { 
  User, Calendar, MapPin, Gift, Check, Clock, AlertCircle, 
  ArrowRight, Ship, Building, UtensilsCrossed 
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import MemberAccountSections from "../components/MemberAccountSections";

const statusConfig = {
  active: { label: "Actif", color: "bg-green-100 text-green-700", icon: Check },
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  expired: { label: "Expiré", color: "bg-red-100 text-red-700", icon: AlertCircle },
  suspended: { label: "Suspendu", color: "bg-gray-100 text-gray-700", icon: AlertCircle },
};

const categoryIcons = {
  cruise: Ship,
  hotel: Building,
  restaurant: UtensilsCrossed,
};

export default function MemberPage() {
  const { user, isAuthenticated, loading: authLoading, isMember } = useAuth();
  const [membership, setMembership] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [membershipRes, dealsRes] = await Promise.all([
        getMyMembership(),
        getDeals({ members_only: true }).catch(() => ({ data: [] }))
      ]);
      setMembership(membershipRes.data);
      setDeals(dealsRes.data || []);
    } catch (error) {
      console.error("Error loading member data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-secondary pt-20 flex items-center justify-center">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />;
  }

  const status = statusConfig[membership?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="member-page">
      {/* Header */}
      <section className="bg-primary text-white py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
              <User className="h-8 w-8" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold">
                Bonjour, {user?.name}
              </h1>
              <p className="text-white/70">{user?.email}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Membership Status */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border rounded-lg p-6">
              <h2 className="font-heading text-lg font-semibold text-primary mb-4">
                Mon adhésion
              </h2>

              {loading ? (
                <div className="space-y-3">
                  <Skeleton className="h-8 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              ) : membership ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                      <StatusIcon className="h-4 w-4" />
                      {status.label}
                    </span>
                  </div>

                  {membership.status === "active" && (
                    <>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>
                          Valide jusqu'au {format(new Date(membership.end_date), "d MMMM yyyy", { locale: fr })}
                        </span>
                      </div>
                      {membership.city && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{membership.city}, {membership.country}</span>
                        </div>
                      )}
                    </>
                  )}

                  {membership.status === "pending" && (
                    <p className="text-sm text-muted-foreground">
                      Votre demande d'adhésion est en cours d'examen. 
                      Nous vous contacterons prochainement.
                    </p>
                  )}

                  {membership.status === "expired" && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-3">
                        Votre adhésion a expiré. Renouvelez-la pour continuer à profiter des avantages.
                      </p>
                      <Button className="w-full bg-accent hover:bg-accent/90">
                        Renouveler
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">
                    Vous n'êtes pas encore adhérent.
                  </p>
                  <Link to="/adherer">
                    <Button className="w-full bg-accent hover:bg-accent/90">
                      Devenir adhérent
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="bg-white border border-border rounded-lg p-6 mt-6">
              <h3 className="font-heading text-lg font-semibold text-primary mb-4">
                Liens rapides
              </h3>
              <div className="space-y-2">
                <Link 
                  to="/bons-plans" 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <Gift className="h-5 w-5 text-accent" />
                  <span>Tous les bons plans</span>
                </Link>
                <Link 
                  to="/recherche" 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                >
                  <Ship className="h-5 w-5 text-accent" />
                  <span>Explorer le blog</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Deals */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-semibold text-primary mb-6">
              Vos avantages exclusifs
            </h2>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array(4).fill(0).map((_, i) => (
                  <Skeleton key={i} className="h-48" />
                ))}
              </div>
            ) : isMember && deals.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {deals.map((deal) => {
                  const Icon = categoryIcons[deal.category] || Gift;
                  
                  return (
                    <div 
                      key={deal.id} 
                      className="bg-white border border-border rounded-lg p-5 hover:border-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-accent" />
                          </div>
                          <div>
                            <p className="font-medium text-primary">{deal.partner_name}</p>
                            {deal.discount && (
                              <p className="text-lg font-bold text-accent">{deal.discount}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="font-semibold text-primary mb-2">{deal.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {deal.description}
                      </p>
                      
                      {deal.procedure && (
                        <div className="pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Pour en profiter :</span> {deal.procedure}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : !isMember ? (
              <div className="bg-white border border-border rounded-lg p-8 text-center">
                <Gift className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-primary mb-2">
                  Avantages réservés aux adhérents
                </h3>
                <p className="text-muted-foreground mb-6">
                  Devenez adhérent pour accéder à tous nos bons plans exclusifs.
                </p>
                <Link to="/adherer">
                  <Button className="bg-accent hover:bg-accent/90">
                    Devenir adhérent
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-white border border-border rounded-lg p-8 text-center">
                <Gift className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-semibold text-primary mb-2">
                  Aucun bon plan pour le moment
                </h3>
                <p className="text-muted-foreground">
                  De nouvelles offres seront bientôt disponibles !
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Adhésion, reçus fiscaux, donations & profil auteur (Phase 13.c) */}
        <div className="mt-10">
          <MemberAccountSections user={user} />
        </div>
      </div>
    </div>
  );
}
